import {useEffect, useRef, useState} from 'react'

/**
 * Mobile submit-gate nudge for the ride pages (Confirm / Edit) — the same behaviour as
 * PhoneBookingConfirmation's Reserve Ride: on a phone, tapping the submit button while a
 * required field is missing scrolls to that field and prints the reason right under it,
 * instead of a disabled button with a tooltip the passenger may not even see.
 *
 * Unlike PhoneBookingConfirmation (whose button is pinned to the bottom of the screen, so
 * it lines the message up against that bar), these pages' buttons sit in the page flow, so
 * the message is simply scrolled to the middle of the view.
 *
 * Usage:
 *   const nudge = useGateNudge()
 *   const active = nudge.activeFor(gateMessage)        // {field, section, message} | null
 *   onClick: if (gateMessage && nudge.isMobile) { nudge.trigger({field, section, message}); return }
 *   <p ref={nudge.errRef} className="md:hidden …">{active.message}</p>   // under the field
 */
export default function useGateNudge() {
    const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 767px)').matches)
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 767px)')
        const sync = () => setIsMobile(mq.matches)
        mq.addEventListener('change', sync)
        return () => mq.removeEventListener('change', sync)
    }, [])

    const errRef = useRef(null)
    const [nudge, setNudge] = useState(null) // {field, section, message, id}
    // The button greys out for a moment after a refused tap — while the page scrolls to the
    // missing field — then goes back to normal.
    const [nudging, setNudging] = useState(false)

    useEffect(() => {
        if (!nudge) return undefined
        const t = setTimeout(() => setNudging(false), 1500)
        return () => clearTimeout(t)
    }, [nudge?.id]) // eslint-disable-line react-hooks/exhaustive-deps

    // After the message renders under its field, bring it into view.
    useEffect(() => {
        if (!nudge) return undefined
        const raf = requestAnimationFrame(() => {
            errRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'})
        })
        return () => cancelAnimationFrame(raf)
    }, [nudge?.id]) // eslint-disable-line react-hooks/exhaustive-deps

    const trigger = (n) => {
        setNudging(true)
        setNudge({...n, id: Date.now()})
    }

    // The nudge only lives while the thing it complains about is still missing.
    const activeFor = (message) => (nudge && message && nudge.message === message ? nudge : null)

    return {isMobile, errRef, nudging, trigger, activeFor}
}
